import { ref, computed, readonly } from 'vue';
import type { Ref } from 'vue';
import type { Player, Letter, LetterKind, SelectionStatus } from '@/types/generated';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';

const TOTAL_LETTERS = 10;

export interface UseLetterSelectionOptions {
  gameId: string;
  players: Player[];
  currentPlayerId: string;
}

export interface SelectionSnapshotData {
  selectedLetters: Letter[];
  selectionEnabled: boolean | null;
  vowelButtonEnabled: boolean | null;
  consonantButtonEnabled: boolean | null;
  selectionStatus: SelectionStatus | null;
  selectorPlayerId: string | null;
}

export interface UseLetterSelectionReturn {
  // State
  selectedLetters: Readonly<Ref<readonly Letter[]>>;
  currentSelector: Readonly<Ref<string>>;
  selectionStatus: Readonly<Ref<SelectionStatus>>;
  vowelButtonEnabled: Readonly<Ref<boolean>>;
  consonantButtonEnabled: Readonly<Ref<boolean>>;

  // Computed
  vowelCount: Readonly<Ref<number>>;
  consonantCount: Readonly<Ref<number>>;
  isPlayerTurn: Readonly<Ref<boolean>>;

  // Methods
  handleSelectionUpdate: (update: SelectionUpdateData) => void;
  setSelectionState: (data: SelectionSnapshotData) => void;
  validateLetterSelection: (letterType: LetterKind) => ValidationResult;
  resetSelection: () => void;
  checkInitializationStatus: () => { hasSelector: boolean; isPlayerTurn: boolean; message: string };
}

export interface SelectionUpdateData {
  letter: Letter | null;
  slot: number | null;
  selector_player_id: string;
  selection_enabled: boolean;
  vowel_button_enabled: boolean;
  consonant_button_enabled: boolean;
  status: SelectionStatus;
}

export interface ValidationResult {
  isValid: boolean;
  reason?: string;
}

export function useLetterSelection(options: UseLetterSelectionOptions): UseLetterSelectionReturn {
  const { players, currentPlayerId } = options;

  // Reactive state
  const selectedLetters = ref<Letter[]>([]);
  const currentSelector = ref<string>('');
  const selectionStatus = ref<SelectionStatus>('WaitingForSelection');
  // Default false: non-selectors must never see enabled buttons on reconnect
  const selectionEnabled = ref(false);

  // Defensive initialization - ensure we have valid players
  if (players.length === 0) {
    reportClientError(new Error('No players provided'), ErrorLocation.LetterSelectionNoPlayers);
  }

  if (!currentPlayerId) {
    reportClientError(
      new Error('No currentPlayerId provided'),
      ErrorLocation.LetterSelectionNoCurrentPlayer
    );
  }

  // Check if currentPlayerId is valid
  const isCurrentPlayerValid = players.some(player => player.id === currentPlayerId);
  if (!isCurrentPlayerValid) {
    reportClientError(
      new Error(`currentPlayerId not found in players list: ${currentPlayerId}`),
      ErrorLocation.LetterSelectionInvalidCurrentPlayer
    );
  }

  // Computed properties
  const vowelCount = computed(() => {
    return selectedLetters.value.filter(letter => letter.kind === 'Vowel').length;
  });

  const consonantCount = computed(() => {
    return selectedLetters.value.filter(letter => letter.kind === 'Consonant').length;
  });

  const isPlayerTurn = computed(() => {
    return currentSelector.value === currentPlayerId;
  });

  // Button states set by server (constraint + selector check already applied)
  const vowelButtonEnabled = ref(false);
  const consonantButtonEnabled = ref(false);

  // Helper method to check for initialization issues
  const checkInitializationStatus = () => {
    if (!currentSelector.value) {
      console.warn('[useLetterSelection] No selector set - waiting for server update');
      return {
        hasSelector: false,
        isPlayerTurn: false,
        message: 'No selector set',
      };
    }

    const isValid = players.some(player => player.id === currentSelector.value);
    if (!isValid) {
      console.warn('[useLetterSelection] Invalid selector:', currentSelector.value);
      return {
        hasSelector: true,
        isPlayerTurn: false,
        message: 'Invalid selector',
      };
    }

    return {
      hasSelector: true,
      isPlayerTurn: currentSelector.value === currentPlayerId,
      message: 'Valid selector',
    };
  };

  // Business logic methods
  const validateLetterSelection = (letterType: LetterKind): ValidationResult => {
    // Check initialization status first
    const initStatus = checkInitializationStatus();
    if (!initStatus.hasSelector) {
      return {
        isValid: false,
        reason: 'Game not ready - waiting for turn assignment',
      };
    }

    // Check if it's the player's turn
    if (!isPlayerTurn.value) {
      return {
        isValid: false,
        reason: 'Not your turn',
      };
    }

    // Check if selection is complete
    if (selectedLetters.value.length >= TOTAL_LETTERS) {
      return {
        isValid: false,
        reason: 'Selection is already complete',
      };
    }

    // Check button enabled state (computed from server's selectionEnabled + constraints)
    if (letterType === 'Vowel' && !vowelButtonEnabled.value) {
      return {
        isValid: false,
        reason: 'Vowel selection is disabled',
      };
    }

    if (letterType === 'Consonant' && !consonantButtonEnabled.value) {
      return {
        isValid: false,
        reason: 'Consonant selection is disabled',
      };
    }

    return { isValid: true };
  };

  const handleSelectionUpdate = (update: SelectionUpdateData) => {
    // Validate selector_player_id
    if (!update.selector_player_id) {
      console.warn(
        '[useLetterSelection] Received empty selector_player_id, keeping current selector'
      );
    } else {
      // Check if the selector_player_id matches a known player
      const isValidPlayer = players.some(player => player.id === update.selector_player_id);
      if (!isValidPlayer) {
        console.warn(
          `[useLetterSelection] Received invalid selector_player_id: ${update.selector_player_id}, known players:`,
          players.map(p => p.id)
        );
      }

      // Update selector (even if invalid, trust server authority)
      currentSelector.value = update.selector_player_id;
    }

    // Update selection enabled (server authority)
    selectionEnabled.value = update.selection_enabled;
    vowelButtonEnabled.value = update.vowel_button_enabled;
    consonantButtonEnabled.value = update.consonant_button_enabled;

    // Update status
    selectionStatus.value = update.status;

    // Handle new letter addition
    if (update.letter && update.slot !== null) {
      // Add letter to the selection
      selectedLetters.value.push(update.letter);
    }
  };

  const setSelectionState = (data: SelectionSnapshotData) => {
    selectedLetters.value = data.selectedLetters;
    if (data.selectorPlayerId !== null) {
      currentSelector.value = data.selectorPlayerId;
    }
    if (data.selectionStatus !== null) {
      selectionStatus.value = data.selectionStatus;
    }
    selectionEnabled.value = data.selectionEnabled ?? false;
    vowelButtonEnabled.value = data.vowelButtonEnabled ?? false;
    consonantButtonEnabled.value = data.consonantButtonEnabled ?? false;
  };

  const resetSelection = () => {
    selectedLetters.value = [];
    currentSelector.value = '';
    selectionStatus.value = 'WaitingForSelection';
    selectionEnabled.value = false;
    vowelButtonEnabled.value = false;
    consonantButtonEnabled.value = false;
  };

  return {
    // State
    selectedLetters: readonly(selectedLetters),
    currentSelector: readonly(currentSelector),
    selectionStatus: readonly(selectionStatus),
    vowelButtonEnabled: readonly(vowelButtonEnabled),
    consonantButtonEnabled: readonly(consonantButtonEnabled),

    // Computed
    vowelCount: readonly(vowelCount),
    consonantCount: readonly(consonantCount),
    isPlayerTurn: readonly(isPlayerTurn),

    // Methods
    handleSelectionUpdate,
    setSelectionState,
    validateLetterSelection,
    resetSelection,
    checkInitializationStatus,
  };
}
